import { ITEM_TYPES } from '../../constants';
import { monthsElapsed, num, toDate } from './dates';

// Debt. A loan is its own debt; real estate carries a mortgage. The two use
// separate field families (`loan*` / `mortgage*`), and debtTermsOf collapses
// them into one shape so nothing below has to branch on which is which.

/** The level payment that amortizes `principal` over `totalMonths`. */
export const calculateMonthlyPayment = (principal, annualInterestRate, totalMonths) => {
    if (principal <= 0 || totalMonths <= 0) return 0;
    if (annualInterestRate === 0) return principal / totalMonths;
    const monthlyRate = annualInterestRate / 12;
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1);
    return isNaN(payment) || !isFinite(payment) ? 0 : payment;
};

/**
 * The instalment the contract specifies — a stored payment wins, otherwise it
 * is derived from the terms.
 *
 * Deliberately blind to dates and to whether the debt still exists: this is the
 * number that *defines* the amortization schedule, and debtBalanceAt replays it
 * to work out what is still owed. If it returned zero for a repaid loan, that
 * replay would report the debt had never been paid down at all.
 *
 * For "what is actually leaving the account", use debtPaymentDue.
 */
export const scheduledPayment = (item) => {
    if (item?.type === ITEM_TYPES.LOAN) {
        return num(item.monthlyPayment
            || calculateMonthlyPayment(num(item.loanAmount), num(item.interestRate), parseInt(item.loanTermTotalMonths || 0)));
    }
    if (item?.type === ITEM_TYPES.REAL_ESTATE && item.hasMortgage) {
        return num(item.mortgageMonthlyPayment
            || calculateMonthlyPayment(num(item.mortgageInitialAmount), num(item.mortgageInterestRate), parseInt(item.mortgageTermTotalMonths || 0)));
    }
    return 0;
};

/**
 * This item's debt as one shape, or null when it has none — or has no start
 * date, in which case there is no schedule to reason about.
 */
export const debtTermsOf = (item) => {
    const isLoan = item?.type === ITEM_TYPES.LOAN && item.startDate;
    const isMortgage = item?.type === ITEM_TYPES.REAL_ESTATE && item.hasMortgage && item.mortgageStartDate;
    if (!isLoan && !isMortgage) return null;

    return isLoan
        ? {
            kind: 'loan',
            startDate: item.startDate,
            initialDebt: num(item.loanAmount),
            interestRate: num(item.interestRate),
            termTotalMonths: parseInt(item.loanTermTotalMonths || 0),
            payment: scheduledPayment(item),
        }
        : {
            kind: 'mortgage',
            startDate: item.mortgageStartDate,
            initialDebt: num(item.mortgageInitialAmount),
            interestRate: num(item.mortgageInterestRate),
            termTotalMonths: parseInt(item.mortgageTermTotalMonths || 0),
            payment: scheduledPayment(item),
        };
};

/**
 * What this debt costs at a given date: the instalment while the term runs,
 * zero once it has ended. Ask this whenever you mean "how much is leaving the
 * account" and a settled debt stops counting on its own.
 *
 * The *term* is what settles it, not the balance. A payment stored rounded to
 * whole units leaves a residue outstanding forever, so a balance-based test
 * treats a 99.99 %-repaid loan as if it were fully in service.
 */
export const debtPaymentDue = (item, asOf = null) => {
    const terms = debtTermsOf(item);
    // Nothing that could have finished — a half-entered item in the form, not
    // a repaid debt. The instalment stands.
    if (!terms || !terms.termTotalMonths) return scheduledPayment(item);

    const elapsed = monthsElapsed(terms.startDate, toDate(asOf) || new Date());
    return elapsed >= terms.termTotalMonths ? 0 : terms.payment;
};

/**
 * Outstanding balance at an arbitrary date, by replaying the amortization from
 * the contract's own start. The single debt truth in the app: the simulations
 * seed from it and then step forward with amortizeDebtMonth, which uses the
 * identical recurrence.
 *
 * Once the schedule is complete, a remainder smaller than one instalment is
 * treated as settled — the final payment closed the account, as a real
 * lender's does. Payments are stored rounded to whole units, so one that
 * rounded down under-pays by a few hellers a month and would otherwise leave a
 * balance outstanding forever: 28 units on a five-year car loan, 187 on a
 * twenty-year mortgage, long after the debt was actually repaid.
 *
 * The "smaller than one instalment" bound is what makes it safe. A payment too
 * small to amortize the loan leaves many instalments outstanding, so its real
 * balance still stands instead of being wiped when the term runs out.
 */
export const debtBalanceAt = (item, asOf = null) => {
    const terms = debtTermsOf(item);
    if (!terms) return 0;

    const monthsPaid = monthsElapsed(terms.startDate, toDate(asOf) || new Date());
    if (monthsPaid === 0) return terms.initialDebt;

    let balance = terms.initialDebt;
    const monthlyInterestRate = terms.interestRate / 12;

    // Never amortize past the end of the term.
    for (let k = 0; k < Math.min(monthsPaid, terms.termTotalMonths); k++) {
        if (balance <= 0) break;
        balance -= terms.payment - balance * monthlyInterestRate;
    }

    const scheduleComplete = monthsPaid >= terms.termTotalMonths;
    if (scheduleComplete && balance > 0 && balance < terms.payment) return 0;

    return Math.max(0, balance);
};

/**
 * Seed the `simulated*` fields that a month loop carries forward. Mutates, as
 * the loops do.
 */
export const initSimulatedDebt = (item, now = new Date()) => {
    item.simulatedLoanBalance = 0;
    item.simulatedMortgageBalance = 0;
    item.simulatedLoanTermRemaining = 0;
    item.simulatedMortgageTermRemaining = 0;

    const terms = debtTermsOf(item);
    if (!terms) return item;

    const remaining = Math.max(0, terms.termTotalMonths - monthsElapsed(terms.startDate, now));

    if (terms.kind === 'loan') {
        item.simulatedLoanBalance = debtBalanceAt(item, now);
        item.simulatedLoanTermRemaining = remaining;
    } else {
        item.simulatedMortgageBalance = debtBalanceAt(item, now);
        item.simulatedMortgageTermRemaining = remaining;
    }
    return item;
};

/**
 * Advance one month of amortization on the simulated balance, and report what
 * was paid so a caller can total up debt service:
 * `{ kind: 'mortgage' | 'loan' | null, payment }`.
 *
 * The `principal > 0 ? principal : payment` fallback is inherited from the
 * original loops: when a month's interest exceeds the payment (a negatively
 * amortizing loan) they reduced the balance by the whole payment rather than
 * letting it grow.
 */
export const amortizeDebtMonth = (item) => {
    const isMortgage = item.type === ITEM_TYPES.REAL_ESTATE && item.hasMortgage && item.simulatedMortgageTermRemaining > 0;
    const isLoan = item.type === ITEM_TYPES.LOAN && item.simulatedLoanTermRemaining > 0;
    if (!isMortgage && !isLoan) return { kind: null, payment: 0 };

    const payment = num(isMortgage ? item.mortgageMonthlyPayment : item.monthlyPayment);
    const monthlyRate = num(isMortgage ? item.mortgageInterestRate : item.interestRate) / 12;
    const balance = isMortgage ? item.simulatedMortgageBalance : item.simulatedLoanBalance;
    const principal = payment - balance * monthlyRate;
    const newBalance = Math.max(0, balance - (principal > 0 ? principal : payment));

    if (isMortgage) {
        item.simulatedMortgageBalance = newBalance;
        item.simulatedMortgageTermRemaining -= 1;
    } else {
        item.simulatedLoanBalance = newBalance;
        item.simulatedLoanTermRemaining -= 1;
    }
    return { kind: isMortgage ? 'mortgage' : 'loan', payment: payment > 0 ? payment : 0 };
};
